import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PersonRelationType } from '@dealstack/shared/lib/generated';
import { RelatedPeopleFormShape } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/utils/relatedPeopleShape.ts';
import {
	bulkCreateRelatedPeopleForPerson,
	getAvailablePeopleToRelateToPerson,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/apis/useRelatedPeople.ts';
import {
	RELATED_PEOPLE_FORM_FIELDS,
	RelatedPeopleFormValues,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/utils/types.ts';
import { PersonDataContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/hooks/usePersonData.ts';

export const useRelatedPeopleDrawer = (onClose: () => void) => {
	const { selectedEntityId, selectedEntityName } = useContext(CapManageControllerContext);
	const { refetchRelatedPeople } = useContext(PersonDataContext);

	const bulkCreateRelatedPeopleMutation = useMutation({
		mutationFn: bulkCreateRelatedPeopleForPerson,
	});

	const relatedPeopleForm = useForm<RelatedPeopleFormValues>({
		mode: 'all',
		resolver: zodResolver(RelatedPeopleFormShape),
	});

	const selectedRelationType = useWatch({
		control: relatedPeopleForm.control,
		name: RELATED_PEOPLE_FORM_FIELDS.relation_type,
	});

	const selectedRelatedPeopleIds = useWatch({
		control: relatedPeopleForm.control,
		name: RELATED_PEOPLE_FORM_FIELDS.person_ids,
	});

	const availablePeopleRequest = useQuery({
		queryKey: ['availablePeopleToRelate', selectedEntityId, selectedRelationType],
		queryFn: () => {
			if (!selectedEntityId || !selectedRelationType) {
				return Promise.resolve([]);
			}

			return getAvailablePeopleToRelateToPerson({
				personId: selectedEntityId,
				relationType: selectedRelationType ?? '',
			});
		},
		enabled: false,
	});

	useEffect(() => {
		void availablePeopleRequest.refetch();
		relatedPeopleForm.setValue(RELATED_PEOPLE_FORM_FIELDS.person_ids, []);
		// A named role describes itself, so the free-text label resets with the type.
		relatedPeopleForm.setValue(RELATED_PEOPLE_FORM_FIELDS.custom_relation_type, '');
	}, [selectedRelationType]);

	const onFormSubmit = async (formValues: RelatedPeopleFormValues) => {
		const { relation_type, person_ids, create_legal_rep, custom_relation_type } = formValues;

		const createLegalRepresentative =
			relation_type === PersonRelationType.PIV && create_legal_rep;

		return bulkCreateRelatedPeopleMutation.mutateAsync(
			{
				personId: selectedEntityId,
				body: {
					personIds: person_ids,
					type: relation_type,
					...(relation_type === PersonRelationType.OTHER
						? { customRelationType: custom_relation_type?.trim() }
						: {}),
					...(createLegalRepresentative ? { createLegalRepresentative: true } : {}),
				},
			},
			{
				onSuccess: () => {
					void refetchRelatedPeople();
					onClose();
				},
			}
		);
	};

	return {
		relatedPeopleForm,
		onFormSubmit,
		isLoading: bulkCreateRelatedPeopleMutation.isPending,
		availablePeople: availablePeopleRequest.data,
		selectedRelatedPeopleIds,
		selectedRelationType,
		selectedEntityName,
	};
};
